<script lang="ts" setup>
import dayjs from 'dayjs';
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import {useProfileDateFormat} from '@/ts/composables/stateful/use-profile-date-format';

const props = defineProps<{
  date: string|Date|dayjs.Dayjs;
  format?: 'date'|'dateAndTime'|'relativeDateTime'|'dayAndMonth'
}>();

// An atom reaching for profile state, which the rest of this layer does not do: the patterns would
// otherwise be drilled through every component between a page and this one, for a value that is the
// same for the whole session. `useProfileDateFormat` carries the reasoning.
//
// The reader's country orders the parts and their language names them, which is where the two axes
// are spelled out (IPD-3229). English keeps its month and weekday names; German stays on digits,
// because no dayjs locale is loaded anywhere in the app and `MMM` would print an English "Apr" on
// the German site.
const patterns = useProfileDateFormat();
const {t} = useI18n();

function getRelativeDateLabel(date: dayjs.Dayjs): string | null {
  const today = dayjs().startOf('day');
  const yesterday = today.subtract(1, 'day');
  const dateStart = date.startOf('day');

  if (dateStart.isSame(today)) {
    return t('today');
  }
  if (dateStart.isSame(yesterday)) {
    return t('yesterday');
  }
  return null;
}

const formattedValue = computed((): string => {
  const date = dayjs(props.date);

  if (props.format === 'relativeDateTime') {
    const relativeLabel = getRelativeDateLabel(date);
    const timeStr = date.format(patterns.value.time);

    if (relativeLabel) {
      return `${relativeLabel}, ${timeStr}`;
    }
    return date.format(patterns.value.dateAndTime);
  }

  const named = props.format === 'dateAndTime' || props.format === 'dayAndMonth';
  return date.format(named ? patterns.value[props.format] : patterns.value.date);
});

// On the template's separator between the date and the slot: it is emitted only when there is
// slot content, because laid out over several lines it also leaked out with an empty slot, which
// reads as "10.08.2025 ," once the date sits inside a sentence. It is an interpolation rather than a
// literal space because the compiler drops a whitespace-only text node that is the first child
// of its parent. The note lives here and not in the template: a comment beside <time> turns the
// SFC into a fragment root, which runtime attribute fallthrough survives (the compiler resolves
// the single element) but @vue/test-utils' wrapper.attributes() does not — this file's own
// datetime assertion is what catches it.
const datetimeAttr = computed(() => {
  if (props.format === 'date' || props.format === 'dayAndMonth') {
    return dayjs(props.date).format('YYYY-MM-DD');
  }
  return dayjs(props.date).format('YYYY-MM-DD HH:mm');
});

</script>

<template>
  <time :datetime="datetimeAttr">{{ formattedValue }}<template v-if="$slots.default">{{ ' ' }}<slot/></template></time>
</template>
